import {
  EXTERNAL_REPORT_DEFAULT_PAGE,
  EXTERNAL_REPORT_ABILITY_DRIVER,
  COMPANY_KIND_PARTNER,
} from '@/consts/externalReports/external_reports';
import { Lov } from '@/models/apis/master/masterResponse';
import type {
  ExternalReportSettings,
  ExternalReportsKeyName,
  ExternalReportPartnerHan,
} from '@/models/apis/externalReports/external_report_settings';
import { reactive } from '@vue/composition-api';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type { ExternalReportCompany } from '@/models/externalReports/externalReportCompany';
import { Master } from '@/models';

interface ExternalReportState {
  externalReportSettings: ExternalReportSettings[];
  externalReportCompanies: ExternalReportCompany[] | null;
  showExternalReportSettings: Record<string, boolean>;
  externalReportAbilities: ExternalReportAbility[];
  showExternalReportSettingsInList: Record<string, boolean>;
  externalReportOnlyType: string | null;
}
interface ExternalReportSelection {
  ability: ExternalReportAbility | null;
  company: ExternalReportCompany | null;
  abilityChoice: ExternalReportsKeyName | null;
  partnerHans: ExternalReportPartnerHan[];
  isDriverAbility: boolean;

}
export interface ExternalReportsResult {
  externalReportState: ExternalReportState;
  setExternalReportSettings: (externalReportSettings: Master) => void;
  canEditExternalReportHan: (externalReportType: string) => boolean;
  checkExternalReportsInput: () => boolean;
  getExternalReportAbility: (externalReportType: string) => ExternalReportAbility;
  getExternalReportPartnerHans: (externalReportType: string) => any[];
  isExternalReportDriverAbility: (externalReportType: string) => boolean;
  getExternalReportCompanyAbilityOptions: (externalReportType: string) => { value: string; label: string }[];
  getExternalReportCompanyAbilityValue: (externalReportType: string) => string;
  onExternalReportCompanyAbilityChange: (externalReportType: string, value: string) => string;
  onExternalReportAbilityTypeChange: (externalReportType: string) => string;
  getExternalReportTypeDefaultPages: (externalReportSettings: ExternalReportSettings[]) => Lov[];
  getShowExternalReportTypeDefaultPages: () => Lov[];
  clearShowExternalReportSettings: () => void;
  resetExternalReportSettings: (externalReportType: string, editingUserId: number) => void;
  resetShowExternalReportSettings: (externalReportAbilities: ExternalReportAbility[]) => void;
  onExternalReportAbilityChange: (externalReportType: string) => void;
  disableAllExternalReports: (editingUserId: number) => void;
  onExternalReportOnlyChange: (
    externalReportType: string,
    isChecked: boolean,
    editingUserId: number,
    onDisableOtherModules: () => void,
  ) => void;
  getExternalReportCompaniesByType: (externalReportType: string) => ExternalReportCompany[];
  hasExternalReportCompanies: (externalReportType: string) => boolean;
  getExternalReportAbilityName: (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ) => string;
  getExternalReportHanName: (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ) => string;
  getExternalReportCompanyName: (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ) => string;
  cloneExternalReportAbilities: (abilities: ExternalReportAbility[]) => ExternalReportAbility[];
  areExternalReportAbilitiesEqual: (
    abilities: ExternalReportAbility[],
    originalAbilities: ExternalReportAbility[],
  ) => boolean;
}
export function useExternalReports(): ExternalReportsResult {
  const externalReportState = reactive<ExternalReportState>({
    externalReportSettings: [],
    showExternalReportSettings: {},
    externalReportCompanies: null,
    externalReportAbilities: [],
    showExternalReportSettingsInList: {},
    externalReportOnlyType: null,
  });
  const setExternalReportSettings = (master: Master): void => {
    externalReportState.externalReportSettings = master.externalReportSettings || [];
    externalReportState.externalReportCompanies = master.externalReportCompanies || [];
    const showSettingsInList: Record<string, boolean> = { ...externalReportState.showExternalReportSettingsInList };
    for (const setting of externalReportState.externalReportSettings) {
      if (!showSettingsInList[setting.external_report_type]) {
        showSettingsInList[setting.external_report_type] = false;
      }
    }
    externalReportState.showExternalReportSettingsInList = showSettingsInList;
  };
  const getExternalReportTypeDefaultPages = (externalReportSettings: ExternalReportSettings[]): Lov[] => {
    const defaultPages: Lov[] = [];
    if (!externalReportSettings) return [];
    for (const reportSetting of externalReportSettings) {
      const reportDefaultPage = reportSetting.common.default_page;
      if (!reportDefaultPage) continue;

      const defaultPage = {...EXTERNAL_REPORT_DEFAULT_PAGE};
      if (reportDefaultPage?.key && reportDefaultPage?.name) {
        defaultPage.key = reportDefaultPage.key;
        defaultPage.val = reportDefaultPage.name;
      }
      defaultPages.push(defaultPage);
    }
    return defaultPages;
  };
  const getAbilityChoice = (externalReportType: string, abilityKey: string): ExternalReportsKeyName | null => {
    const setting = getExternalReportTypeSettings(externalReportType);
    if (!setting) return null;

    return setting.choices.abilities.find(choice => choice.key === abilityKey) ?? null;
  };
  const getSelection = (externalReportType: string): ExternalReportSelection => {
    const ability = getExternalReportAbility(externalReportType);
    const company = getExternalReportSelectedCompany(externalReportType);
    const abilityChoice = getAbilityChoice(externalReportType, ability.ability_id);
    const partnerHans = company?.partner_han_masters || [];
    const isDriverAbility = !!abilityChoice?.name?.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER);
    return { ability, company, abilityChoice, partnerHans, isDriverAbility };
  };
  const getExternalReportDefaultPageKey = (): string => {
    const pages = getShowExternalReportTypeDefaultPages();
    return pages.length > 0 ? pages[0].key : '';
  };
  const getShowExternalReportTypeDefaultPages = (): Lov[] => {
    const defaultPages: Lov[] = [];
    if (!externalReportState.showExternalReportSettings) return [];
    for (const reportType of Object.keys(externalReportState.showExternalReportSettings)) {
      if (!externalReportState.showExternalReportSettings[reportType]) continue;
      if (!canEditExternalReportHan(reportType)) continue;

      const menuOperationManagement = externalReportState.externalReportSettings?.find(
        (setting) => setting.external_report_type === reportType,
      )?.common.default_page;
      const defaultPage = {...EXTERNAL_REPORT_DEFAULT_PAGE};
      if (menuOperationManagement?.key && menuOperationManagement?.name) {
        defaultPage.key = menuOperationManagement.key;
        defaultPage.val = menuOperationManagement.name;
      }
      defaultPages.push(defaultPage);
    }
    return defaultPages;
  };
  const getExternalReportTypeSettings = (externalReportType: string): ExternalReportSettings | null => {
    const externalReportSetting = externalReportState.externalReportSettings?.find(
      externalReport => externalReport.external_report_type === externalReportType,
    );
    if (!externalReportSetting) return null;

    return externalReportSetting;
  };
  const resetExternalReportSettings = (externalReportType: string, editingUserId: number): void => {
    if (!Array.isArray(externalReportState.externalReportAbilities)) {
      externalReportState.externalReportAbilities = [];
    }

    if (externalReportState.showExternalReportSettings[externalReportType]) {
      const exists = externalReportState.externalReportAbilities.find(
        ability => ability.external_report_type === externalReportType,
      );
      if (!exists) {
        externalReportState.externalReportAbilities.push({
          user_id: editingUserId,
          ability_id: '',
          external_report_type: externalReportType,
          han_name: null,
          company_id: null,
          use_this_external_report_only: false,
        });
      }
    } else {
      externalReportState.externalReportAbilities = externalReportState.externalReportAbilities.filter(
        ability => ability.external_report_type !== externalReportType,
      );
    }
  };
  const disableAllExternalReports = (editingUserId: number): void => {
    const reportTypes = new Set([
      ...Object.keys(externalReportState.showExternalReportSettings || {}),
      ...((externalReportState.externalReportSettings || []).map(setting => setting.external_report_type)),
    ]);
    reportTypes.forEach((reportType) => {
      if (!externalReportState.showExternalReportSettings[reportType]) return;
      externalReportState.showExternalReportSettings[reportType] = false;
      resetExternalReportSettings(reportType, editingUserId);
    });
    externalReportState.externalReportAbilities = externalReportState.externalReportAbilities.map((ability) => ({
      ...ability,
      use_this_external_report_only: false,
    }));
    externalReportState.externalReportOnlyType = null;
  };
  const onExternalReportOnlyChange = (
    externalReportType: string,
    isChecked: boolean,
    editingUserId: number,
    onDisableOtherModules: () => void,
  ): void => {
    if (!isChecked) {
      externalReportState.externalReportOnlyType = null;
      const ability = getExternalReportAbility(externalReportType);
      ability.use_this_external_report_only = false;
      return;
    }
    externalReportState.externalReportOnlyType = externalReportType;

    const reportTypes = new Set([
      ...Object.keys(externalReportState.showExternalReportSettings || {}),
      ...((externalReportState.externalReportSettings || []).map(setting => setting.external_report_type)),
    ]);
    reportTypes.forEach((reportType) => {
      const shouldEnable = reportType === externalReportType;
      externalReportState.showExternalReportSettings[reportType] = shouldEnable;
      resetExternalReportSettings(reportType, editingUserId);
    });

    externalReportState.externalReportAbilities = externalReportState.externalReportAbilities.map((ability) => ({
      ...ability,
      use_this_external_report_only: ability.external_report_type === externalReportType,
    }));

    onDisableOtherModules();
  };
  const canEditExternalReportHan = (externalReportType: string): boolean => {
    const { abilityChoice, isDriverAbility } = getSelection(externalReportType);
    return !!abilityChoice && isDriverAbility;
  };
  const checkExternalReportsInput = (): boolean => {
    for (const [externalReportType, isEnabled] of Object.entries(externalReportState.showExternalReportSettings)) {
      if (!isEnabled) continue;

      const externalReportAbilities = Array.isArray(externalReportState.externalReportAbilities)
        ? externalReportState.externalReportAbilities
        : [];
      const ability = externalReportAbilities.find(ability => ability.external_report_type === externalReportType);

      if (!ability?.ability_id) {
        return false;
      }

      const { partnerHans, isDriverAbility } = getSelection(externalReportType);
      const requiresHan = isDriverAbility && partnerHans.length > 0;
      if (requiresHan && !ability.han_name) return false;
    }
    return true;
  };
  const getExternalReportCompaniesByType = (externalReportType: string): ExternalReportCompany[] => {
    const companies = externalReportState.externalReportCompanies || [];
    return companies.filter((company) => company.external_report_type === externalReportType);
  };
  const hasExternalReportCompanies = (externalReportType: string) => {
    return getExternalReportCompaniesByType(externalReportType).length > 0;
  };
  const getExternalReportSelectedCompany = (externalReportType: string): ExternalReportCompany | null => {
    const ability = getExternalReportAbility(externalReportType);
    if (!ability?.company_id) return null;
    const companies = externalReportState.externalReportCompanies || [];
    return companies.find((company) =>
      company.external_report_type === externalReportType &&
      Number(company.id) === Number(ability.company_id),
    ) || null;
  };
  const getExternalReportPartnerHans = (externalReportType: string): ExternalReportPartnerHan[] => {
    const company = getExternalReportSelectedCompany(externalReportType);
    if (!company) return [];
    return company.partner_han_masters;
  };
  const canSelectExternalReportAbility = (
    externalReportType: string,
    ability: ExternalReportsKeyName,
    companyOverride: ExternalReportCompany | null,
  ): boolean => {
    const choice = getAbilityChoice(externalReportType, ability.key);
    if (!choice) return false;
    const company = companyOverride ?? getExternalReportSelectedCompany(externalReportType);
    const isDriver = !!choice.name?.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER);
    return !isDriver || (!!company && company.kind === COMPANY_KIND_PARTNER);
  };
  const getExternalReportCompanyAbilityOptions = (externalReportType: string): { value: string; label: string }[] => {
    const companies = getExternalReportCompaniesByType(externalReportType);
    const setting = getExternalReportTypeSettings(externalReportType);
    if (!setting) return [];
    const abilities = setting.choices.abilities || [];
    const options: { value: string; label: string }[] = [];
    for (const company of companies) {
      const availableAbilities = abilities.filter(ability =>
        canSelectExternalReportAbility(externalReportType, ability, company),
      );
      if (!availableAbilities.length) continue;

      if (company.kind === COMPANY_KIND_PARTNER) {
        options.push(
          ...availableAbilities.map(ability => ({
            value: `${company.id}:${ability.key}`,
            label: `${company.alias_name}(${ability.name || ability.key})`,
          })),
        );
      } else {
        const ability = availableAbilities[0];
        options.push({
          value: `${company.id}:${ability?.key || ''}`,
          label: company.alias_name,
        });
      }
    }
    return options;
  };
  const getExternalReportCompanyAbilityValue = (externalReportType: string): string => {
    const ability = getExternalReportAbility(externalReportType);
    const companyId = ability.company_id ?? '';
    const abilityId = ability.ability_id ?? '';
    return `${companyId}:${abilityId}`;
  };
  const onExternalReportCompanyAbilityChange = (externalReportType: string, value: string): string => {
    const ability = getExternalReportAbility(externalReportType);
    const [companyIdStr = '', abilityKey = ''] = `${value}`.split(':');
    ability.company_id = companyIdStr ? Number(companyIdStr) : null;
    ability.ability_id = abilityKey;
    ability.han_name = null;
    if (isExternalReportDriverAbility(externalReportType)) {
      ability.use_this_external_report_only = false;
      externalReportState.externalReportOnlyType = null;
    }
    return onExternalReportAbilityTypeChange(externalReportType);
  };
  const isExternalReportDriverAbility = (externalReportType: string): boolean => {
    const { isDriverAbility } = getSelection(externalReportType);
    return isDriverAbility;
  };
  const onExternalReportAbilityTypeChange = (externalReportType: string): string => {
    onExternalReportAbilityChange(externalReportType);
    return getExternalReportDefaultPageKey();
  };
  const clearShowExternalReportSettings = (): void => {
    externalReportState.showExternalReportSettings = {};
  };
  const resetShowExternalReportSettings = (externalReportAbilities: ExternalReportAbility[]): void => {
    externalReportState.showExternalReportSettings = externalReportAbilities?.reduce((acc, ability) => {
      acc[ability.external_report_type] = true;
      return acc;
    }, {} as Record<string, boolean>) || {};
  };
  const findExternalReportAbility = (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ): ExternalReportAbility | null => {
    if (!Array.isArray(externalReportAbilities)) return null;
    return externalReportAbilities.find(
      item => item.external_report_type === externalReportType,
    ) || null;
  };
  const getExternalReportAbility = (externalReportType: string): ExternalReportAbility =>
    externalReportState.externalReportAbilities.find(
      item => item.external_report_type === externalReportType,
    ) || {
      user_id: 0,
      ability_id: '',
      external_report_type: '',
      company_id: null,
      han_name: null,
      use_this_external_report_only: false,
    };
  const onExternalReportAbilityChange = (externalReportType: string): void => {
    const abilities = externalReportState.externalReportAbilities;
    if (!abilities && !Array.isArray(abilities)) return;

    const ability = abilities.find(item => item.external_report_type === externalReportType);
    if (!ability || hasExternalReportCompanies(externalReportType)) return;

    ability.han_name = null;
  };
  const getExternalReportAbilityName = (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ): string => {
    const ability = findExternalReportAbility(externalReportAbilities, externalReportType);
    if (!ability || !ability.ability_id) return '';

    const abilityChoice = getAbilityChoice(externalReportType, ability.ability_id);
    return abilityChoice?.name || ability.ability_id || '';
  };
  const getExternalReportHanName = (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ): string => {
    const ability = findExternalReportAbility(externalReportAbilities, externalReportType);
    return ability?.han_name || '';
  };
  const getExternalReportCompanyName = (
    externalReportAbilities: ExternalReportAbility[],
    externalReportType: string,
  ): string => {
    const ability = findExternalReportAbility(externalReportAbilities, externalReportType);
    if (!ability?.company_id) return '';
    const companies = externalReportState.externalReportCompanies || [];
    const company = companies.find((company) =>
      company.external_report_type === externalReportType &&
      Number(company.id) === Number(ability.company_id),
    );
    return company?.alias_name || '';
  };
  const cloneExternalReportAbilities = (abilities?: ExternalReportAbility[]) =>
    Array.isArray(abilities)
      ? abilities.map(ability => ({
        ...ability,
        use_this_external_report_only: ability.use_this_external_report_only ?? false,
      })) : [];
  const areExternalReportAbilitiesEqual = (
    abilities: ExternalReportAbility[],
    originalAbilities: ExternalReportAbility[],
  ): boolean => {
    if (!abilities && !originalAbilities) return true;
    if (!abilities || !originalAbilities) return false;

    const clonedAbilities = cloneExternalReportAbilities(abilities);
    const clonedOriginalAbilities = cloneExternalReportAbilities(originalAbilities);
    if (clonedAbilities.length !== clonedOriginalAbilities.length) return false;

    const normalize = (abilities: ExternalReportAbility[]) => abilities
      .map((ability) => ({
        user_id: ability.user_id,
        ability_id: ability.ability_id || '',
        external_report_type: ability.external_report_type,
        han_name: ability.han_name ?? null,
        use_this_external_report_only: ability.use_this_external_report_only ?? false,
      }))
      .sort((a, b) => {
        if (a.external_report_type !== b.external_report_type) {
          return a.external_report_type.localeCompare(b.external_report_type);
        }
        if (a.ability_id !== b.ability_id) {
          return a.ability_id.localeCompare(b.ability_id);
        }
        const hanNameA = a.han_name ?? '';
        const hanNameB = b.han_name ?? '';
        if (hanNameA !== hanNameB) {
          return hanNameA.localeCompare(hanNameB);
        }
        return `${a.user_id ?? ''}`.localeCompare(`${b.user_id ?? ''}`);
      });
    const normalized = normalize(clonedAbilities);
    const originalNormalized = normalize(clonedOriginalAbilities);
    return normalized.every((ability, index) => {
      const counterpart = originalNormalized[index];
      return ability.user_id === counterpart.user_id &&
        ability.ability_id === counterpart.ability_id &&
        ability.external_report_type === counterpart.external_report_type &&
        ability.han_name === counterpart.han_name &&
        ability.use_this_external_report_only === counterpart.use_this_external_report_only;
    });
  };

  return {
    externalReportState,
    setExternalReportSettings,
    canEditExternalReportHan,
    checkExternalReportsInput,
    getExternalReportAbility,
    getExternalReportPartnerHans,
    isExternalReportDriverAbility,
    getExternalReportCompanyAbilityOptions,
    getExternalReportCompanyAbilityValue,
    onExternalReportCompanyAbilityChange,
    getExternalReportTypeDefaultPages,
    getShowExternalReportTypeDefaultPages,
    resetExternalReportSettings,
    clearShowExternalReportSettings,
    cloneExternalReportAbilities,
    resetShowExternalReportSettings,
    onExternalReportAbilityChange,
    onExternalReportAbilityTypeChange,
    onExternalReportOnlyChange,
    disableAllExternalReports,
    getExternalReportAbilityName,
    getExternalReportHanName,
    getExternalReportCompanyName,
    getExternalReportCompaniesByType,
    hasExternalReportCompanies,
    areExternalReportAbilitiesEqual,
  };
}
